import { useNavigate } from 'react-router-dom';
import { ExportAccess, ExportReportType, useCreateExportJobMutation } from 'src/graphql/_generated';
import { showReportBuildingToast, showReportFailedToast } from 'src/components/Toast';
import { subscribeToExportJob } from 'src/realtime/exportSocket';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import type { ExportReportTypeAnalytics } from 'src/utils/mixpanel/types';

// Mirrors the export-runner's DocConfigSchema discriminated union — kept as a
// hand-rolled type because the runner's Zod schema isn't shared across repos.
// Runner source: export-runner-server/export-runner/src/config/docConfig.ts.
//
// The BE's `reportType` enum (ExportReportType) is the entity type
// (ASSESSMENT | SCENARIO | RMP | INSIGHTS); we derive it 1:1 from docConfig.type.
// The file format lives inside docConfig.format and is interpreted by the runner.

export interface IncludesActions {
  enabled: boolean;
  completionStatus: 'ALL' | 'COMPLETED' | 'INCOMPLETED';
}

export interface AssessmentDocConfig {
  type: 'assessment';
  format: 'pdf';
  assessmentId: string;
  isLogarithmic: boolean;
  // Empty array means "all scenarios" on the runner side.
  renderScenarios: string[];
  includesExecSummary: boolean;
  includesScenarios: boolean;
  includesRMP: boolean;
  includesIncidents: boolean;
  includesRiskTolerance: boolean;
  includesActions: IncludesActions;
}

export interface ScenarioDocConfig {
  type: 'scenario';
  format: 'pdf';
  scenarioId: string;
  // Used by the runner only for the external title link; not for data fetching.
  assessmentId: string;
  isLogarithmic: boolean;
  includesRMP: boolean;
  includesIncidents: boolean;
  includesActions: IncludesActions;
}

export interface RmpDocConfig {
  type: 'rmp';
  format: 'pdf';
  controlIds: string[];
  includesActions: IncludesActions;
}

export interface InsightsDocConfig {
  type: 'insights';
  format: 'pdf';
  assessmentId: string;
}

// The board "Management Report" deck. The runner builds it from these two ids
// (one combined query) and dispatches on docConfig.type, so it rides the same
// createExportJob flow as the other reports — pptx landscape by default.
export interface ManagementReportDocConfig {
  type: 'boardAndInvestment';
  format: 'pptx';
  assessmentId: string;
  workspaceId: string;
}

export type ExportDocConfig =
  | AssessmentDocConfig
  | ScenarioDocConfig
  | RmpDocConfig
  | InsightsDocConfig
  | ManagementReportDocConfig;

interface UseExportJobOptions {
  // Closed only on success; on failure the modal stays open so the user keeps
  // their selections and can retry.
  onClose: () => void;
}

interface TriggerExportOptions {
  // Workspace-wide visibility (true) vs requesting-user-only (false).
  // BE made `access` non-null; if omitted, we default to private (USER) — the
  // safer fallback so a missed toggle never accidentally publishes.
  isPublic?: boolean;
}

// Maps docConfig.type (entity) to the BE's ExportReportType enum.
function reportTypeFromDocConfig(type: ExportDocConfig['type']): ExportReportType {
  switch (type) {
    case 'assessment':
      return ExportReportType.Assessment;
    case 'scenario':
      return ExportReportType.Scenario;
    case 'rmp':
      return ExportReportType.Rmp;
    case 'insights':
      return ExportReportType.Insights;
    // The management report is an assessment-level deck; reportType is only
    // metadata (audit/email naming, reads docConfig.assessmentId). The runner
    // builds the board deck from docConfig.type === 'boardAndInvestment'.
    case 'boardAndInvestment':
      return ExportReportType.Assessment;
  }
}

// Same mapping for mixpanel — uppercase entity types for analytics consistency.
function reportTypeAnalytics(type: ExportDocConfig['type']): ExportReportTypeAnalytics {
  switch (type) {
    case 'assessment':
      return 'ASSESSMENT';
    case 'scenario':
      return 'SCENARIO';
    case 'rmp':
      return 'RMP';
    case 'insights':
      return 'INSIGHTS';
    case 'boardAndInvestment':
      return 'ASSESSMENT';
  }
}

export function useExportJob({ onClose }: UseExportJobOptions) {
  const navigate = useNavigate();
  const [createExportJob, { loading }] = useCreateExportJobMutation({
    onCompleted: (data) => {
      // Start streaming progress for this job over WS so the Report Centre row
      // updates live without waiting for the 5-min poll. Auto-tears down on
      // terminal event; safe to fire-and-forget.
      if (data.createExportJob?.id) {
        subscribeToExportJob(data.createExportJob.id);
      }
      // Yellow "building" toast — the file isn't ready yet, the runner is
      // generating it. "Success" would mislead the user into expecting a download.
      showReportBuildingToast({
        onCtaClick: () => navigate('/report-centre'),
      });
      onClose();
    },
    onError: (err) => {
      // Surface the BE message when present (e.g. "Unable to launch export
      // task") — usually more actionable than our generic fallback.
      const serverMsg = err.graphQLErrors?.[0]?.message;
      showReportFailedToast(serverMsg ? { subtitle: serverMsg } : undefined);
    },
  });

  const triggerExport = async (docConfig: ExportDocConfig, options?: TriggerExportOptions) => {
    const access = options?.isPublic ? ExportAccess.Workspace : ExportAccess.User;

    const result = await createExportJob({
      variables: {
        reportType: reportTypeFromDocConfig(docConfig.type),
        reportParams: JSON.stringify(docConfig),
        access,
      },
    });

    // Fire only on success path — rejected promises (BE/network errors) skip
    // this entirely; the hook's onError already handles user-facing feedback.
    mpEvent(MPEvents.ExportRequested, {
      reportType: reportTypeAnalytics(docConfig.type),
      format: docConfig.format,
      access: access === ExportAccess.Workspace ? 'WORKSPACE' : 'USER',
    });

    return result;
  };

  return { triggerExport, loading };
}
